import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import useStatesUniversities from "../../hooks/useStatesUniversities";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
};

const REMINDER_INTERVAL_OPTIONS = [10080, 1440, 720, 60, 30, 15];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => i); // 0..59
const scrollMenuProps = { PaperProps: { style: { maxHeight: 260 } } };

// Splits a 24h "HH:mm" string into 12h hour/minute/AM-PM parts for display,
// and rebuilds a 24h "HH:mm" string from those parts on change.
const parseTime24 = (value) => {
  if (!value) return { hour: 9, minute: 0, period: "AM" };
  const [h, m] = value.split(":").map(Number);
  const hour24 = Number.isFinite(h) ? h : 0;
  const minute = Number.isFinite(m) ? m : 0;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour: hour12, minute, period };
};

const toTime24 = (hour12, minute, period) => {
  let hour24 = hour12 % 12;
  if (period === "PM") hour24 += 12;
  return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

function TimeFieldAmPm({ label, value, onChange, error, helperText }) {
  const { hour, minute, period } = parseTime24(value);

  const update = (nextHour, nextMinute, nextPeriod) =>
    onChange(toTime24(nextHour, nextMinute, nextPeriod));

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="caption"
        sx={{
          color: error ? "error.main" : "text.secondary",
          display: "block",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <FormControl size="small" error={error} sx={{ minWidth: 68 }}>
          <Select
            value={hour}
            onChange={(e) => update(Number(e.target.value), minute, period)}
            MenuProps={scrollMenuProps}
          >
            {HOURS_12.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" error={error} sx={{ minWidth: 68 }}>
          <Select
            value={minute}
            onChange={(e) => update(hour, Number(e.target.value), period)}
            MenuProps={scrollMenuProps}
          >
            {MINUTES_60.map((m) => (
              <MenuItem key={m} value={m}>
                {String(m).padStart(2, "0")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" error={error} sx={{ minWidth: 80 }}>
          <Select
            value={period}
            onChange={(e) => update(hour, minute, e.target.value)}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {helperText && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.3 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

const formatReminderLabel = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n)) return String(minutes);
  if (n % 10080 === 0) return `${n / 10080} week${n / 10080 > 1 ? "s" : ""} before`;
  if (n % 1440 === 0) return `${n / 1440} day${n / 1440 > 1 ? "s" : ""} before`;
  if (n % 60 === 0) return `${n / 60} hour${n / 60 > 1 ? "s" : ""} before`;
  return `${n} min before`;
};

const emptyForm = {
  examName: "",
  examType: "",
  examDate: "",
  examTime: "",
  examEndTime: "",
  universityId: "",
  courseId: "",
  semesterId: "",
  regulationYearId: "",
  bookId: "",
  venue: "",
  instructions: "",
  durationMinutes: "",
  reminderIntervals: [],
  autoScheduleReminders: true,
};

function ExamTimeTableModal({ open, onClose, exam, isNew, onSuccess }) {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Local-only filter, never sent to the backend
  const [stateId, setStateId] = useState("");

  const [allCourses, setAllCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [regulationYears, setRegulationYears] = useState([]);
  const [books, setBooks] = useState([]);

  const { states, universities, loadingStates, loadingUniversities } =
    useStatesUniversities(stateId);

  useEffect(() => {
    api
      .get("/getCourses", { params: { active: true } })
      .then((res) => setAllCourses(res.data?.data ?? []))
      .catch((err) => console.error("Courses fetch error:", err));
    api
      .get("/getSemesters")
      .then((res) => setSemesters(res.data?.data ?? []))
      .catch((err) => console.error("Semesters fetch error:", err));
    api
      .get("/api/v1/books")
      .then((res) => setBooks(res.data?.data ?? []))
      .catch((err) => console.error("Books fetch error:", err));
  }, []);

  // Fetch regulation years once course + university are selected
  useEffect(() => {
    if (formData.courseId && formData.universityId) {
      api
        .get(
          `/api/regulation-years/course/${formData.courseId}/university/${formData.universityId}`,
        )
        .then((res) => setRegulationYears(res.data?.data ?? []))
        .catch((err) => {
          console.error("Failed to fetch regulation years:", err);
          setRegulationYears([]);
        });
    } else {
      setRegulationYears([]);
    }
  }, [formData.courseId, formData.universityId]);

  // Populate form on edit / reset on add
  useEffect(() => {
    if (exam) {
      setFormData({
        examName: exam.examName || "",
        examType: exam.examType || "",
        examDate: exam.examDate || "",
        examTime: exam.examTime || "",
        examEndTime: exam.examEndTime || "",
        universityId: exam.universityId || "",
        courseId: exam.courseId || "",
        semesterId: exam.semesterId || "",
        regulationYearId: exam.regulationYearId || "",
        bookId: exam.bookId || "",
        venue: exam.venue || "",
        instructions: exam.instructions || "",
        durationMinutes: exam.durationMinutes ?? "",
        reminderIntervals: exam.reminderIntervals || [],
        autoScheduleReminders: exam.autoScheduleReminders ?? true,
      });
      setStateId(exam.stateId || "");
    } else {
      setFormData(emptyForm);
      setStateId("");
    }
    setErrors({});
  }, [exam, open]);

  const handleStateChange = (e) => {
    setStateId(e.target.value);
    setFormData((prev) => ({
      ...prev,
      universityId: "",
      courseId: "",
      semesterId: "",
      regulationYearId: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "universityId") {
        return {
          ...prev,
          universityId: value,
          courseId: "",
          semesterId: "",
          regulationYearId: "",
        };
      }
      if (name === "courseId") {
        return {
          ...prev,
          courseId: value,
          semesterId: "",
          regulationYearId: "",
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const coursesForUniversity = formData.universityId
    ? allCourses.filter(
        (c) => String(c.universityId) === String(formData.universityId),
      )
    : allCourses;

  const selectedCourse = allCourses.find(
    (c) => String(c.id) === String(formData.courseId),
  );

  const semestersForCourse = selectedCourse?.semesters?.length
    ? semesters.filter((s) =>
        selectedCourse.semesters.some((n) => String(n) === String(s.id)),
      )
    : semesters;

  const validate = () => {
    const newErrors = {};
    [
      "examName",
      "examDate",
      "examTime",
      "examEndTime",
      "universityId",
      "courseId",
      "semesterId",
    ].forEach((field) => {
      if (!formData[field]) newErrors[field] = "Required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      examName: formData.examName.trim(),
      examType: formData.examType.trim(),
      examDate: formData.examDate,
      examTime: formData.examTime,
      examEndTime: formData.examEndTime,
      universityId: Number(formData.universityId),
      courseId: Number(formData.courseId),
      semesterId: Number(formData.semesterId),
      regulationYearId: formData.regulationYearId
        ? Number(formData.regulationYearId)
        : null,
      bookId: formData.bookId ? Number(formData.bookId) : null,
      venue: formData.venue.trim(),
      instructions: formData.instructions.trim(),
      durationMinutes: formData.durationMinutes
        ? Number(formData.durationMinutes)
        : null,
      reminderIntervals: formData.reminderIntervals.map(Number),
      autoScheduleReminders: formData.autoScheduleReminders,
    };

    setIsLoading(true);
    try {
      if (isNew) {
        await api.post("/api/admin/exams", payload);
        toast.success("Exam created successfully.");
      } else {
        await api.put(`/api/admin/exams/${exam.id}`, payload);
        toast.success("Exam updated successfully.");
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to save exam. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box sx={{ ...modalStyle, width: { xs: 340, md: 560 } }}>
        <Typography
          variant="h6"
          sx={{
            px: 4,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          {isNew ? "Add Exam" : "Edit Exam"}
        </Typography>

        <Box sx={{ px: 4, py: 2, overflowY: "auto", flex: 1 }}>
          <TextField
            label="Exam Name"
            name="examName"
            fullWidth
            margin="normal"
            size="small"
            value={formData.examName}
            onChange={handleChange}
            error={!!errors.examName}
            helperText={errors.examName}
          />

          <TextField
            label="Exam Type"
            name="examType"
            fullWidth
            margin="normal"
            size="small"
            placeholder="e.g. SEM EXAM"
            value={formData.examType}
            onChange={handleChange}
          />

          <TextField
            label="Exam Date"
            name="examDate"
            type="date"
            fullWidth
            margin="normal"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={formData.examDate}
            onChange={handleChange}
            error={!!errors.examDate}
            helperText={errors.examDate}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <TimeFieldAmPm
              label="Start Time"
              value={formData.examTime}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, examTime: v }))
              }
              error={!!errors.examTime}
              helperText={errors.examTime}
            />
            <TimeFieldAmPm
              label="End Time"
              value={formData.examEndTime}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, examEndTime: v }))
              }
              error={!!errors.examEndTime}
              helperText={errors.examEndTime}
            />
          </Box>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>State</InputLabel>
            <Select
              name="stateId"
              value={stateId}
              onChange={handleStateChange}
              label="State"
              disabled={loadingStates}
            >
              {states.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            margin="normal"
            size="small"
            error={!!errors.universityId}
          >
            <InputLabel>University</InputLabel>
            <Select
              name="universityId"
              value={formData.universityId}
              onChange={handleChange}
              label="University"
              disabled={!stateId || loadingUniversities}
            >
              {universities.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            margin="normal"
            size="small"
            error={!!errors.courseId}
          >
            <Autocomplete
              size="small"
              disabled={!formData.universityId}
              options={coursesForUniversity}
              getOptionLabel={(option) => option.courseName || ""}
              value={
                coursesForUniversity.find(
                  (c) => c.id === formData.courseId,
                ) || null
              }
              onChange={(event, newValue) =>
                handleChange({
                  target: { name: "courseId", value: newValue ? newValue.id : "" },
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Course"
                  error={!!errors.courseId}
                  helperText={errors.courseId}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <FormControl
            fullWidth
            margin="normal"
            size="small"
            error={!!errors.semesterId}
          >
            <InputLabel>Semester</InputLabel>
            <Select
              name="semesterId"
              value={formData.semesterId}
              onChange={handleChange}
              label="Semester"
              disabled={!formData.courseId}
            >
              {semestersForCourse.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Regulation Year</InputLabel>
            <Select
              name="regulationYearId"
              value={formData.regulationYearId}
              onChange={handleChange}
              label="Regulation Year"
              disabled={!formData.courseId || !formData.universityId}
            >
              {regulationYears.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.regulationName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size="small">
            <Autocomplete
              options={books}
              getOptionLabel={(option) =>
                `${option.id} - ${option.title || "Untitled"}`
              }
              value={books.find((b) => b.id === formData.bookId) || null}
              onChange={(event, newValue) =>
                handleChange({
                  target: { name: "bookId", value: newValue ? newValue.id : "" },
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Book" size="small" />
              )}
            />
          </FormControl>

          <TextField
            label="Duration (minutes)"
            name="durationMinutes"
            type="number"
            fullWidth
            margin="normal"
            size="small"
            value={formData.durationMinutes}
            onChange={handleChange}
          />

          <TextField
            label="Venue"
            name="venue"
            fullWidth
            margin="normal"
            size="small"
            value={formData.venue}
            onChange={handleChange}
          />

          <TextField
            label="Instructions"
            name="instructions"
            fullWidth
            margin="normal"
            size="small"
            multiline
            rows={2}
            value={formData.instructions}
            onChange={handleChange}
          />

          <Autocomplete
            multiple
            freeSolo
            options={REMINDER_INTERVAL_OPTIONS}
            getOptionLabel={(option) => formatReminderLabel(option)}
            value={formData.reminderIntervals}
            onChange={(event, newValue) =>
              setFormData((prev) => ({
                ...prev,
                reminderIntervals: newValue
                  .map((v) => Number(v))
                  .filter((v) => Number.isFinite(v) && v > 0),
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reminder Intervals (minutes before exam)"
                margin="normal"
                size="small"
                placeholder="e.g. 1440 for 1 day before, press Enter"
              />
            )}
          />

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={formData.autoScheduleReminders}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    autoScheduleReminders: e.target.checked,
                  }))
                }
              />
            }
            label="Auto-schedule Reminders"
          />
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} color="secondary" sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default ExamTimeTableModal;
